/**
 * 🌍 Location Data Service
 *
 * Service for fetching and caching location data (countries and states/provinces)
 * from the backend API. Provides a centralized way to access location data
 * across the application.
 */

import { apiClient } from './api'

// Types
export interface State {
  code: string
  name: string
}

export interface Country {
  code: string
  name: string
  states: State[]
}

export interface LocationData {
  countries: Country[]
}

export interface CountryData {
  country: Country
}

// Cache for location data
let locationDataCache: LocationData | null = null
let cacheTimestamp: number | null = null
const CACHE_DURATION = 60 * 60 * 1000 // 1 hour in milliseconds

/**
 * Fetch all location data (countries and their states)
 * Uses caching to avoid repeated API calls
 */
export const getLocationData = async (): Promise<LocationData> => {
  // Check if cache is valid
  if (
    locationDataCache &&
    cacheTimestamp &&
    Date.now() - cacheTimestamp < CACHE_DURATION
  ) {
    return locationDataCache
  }

  try {
    const response = await apiClient.get<LocationData>('/admin/franchise-partners/locations')
    locationDataCache = response.data
    cacheTimestamp = Date.now()
    return response.data
  } catch (error) {
    console.error('Failed to fetch location data:', error)
    // Return empty data as fallback
    return { countries: [] }
  }
}

/**
 * Fetch location data for a specific country
 * @param countryCode - Country code (AU, NZ, US, USA)
 */
export const getCountryData = async (countryCode: string): Promise<Country | null> => {
  try {
    const response = await apiClient.get<CountryData>(
      `/admin/franchise-partners/locations?country=${countryCode}`
    )
    return response.data.country
  } catch (error) {
    console.error(`Failed to fetch data for country ${countryCode}:`, error)
    return null
  }
}

/**
 * Get states for a specific country from cache or API
 * @param countryCode - Country code (AU, NZ, US, USA)
 */
export const getStatesForCountry = async (countryCode: string): Promise<State[]> => {
  // Normalize country code
  const normalizedCode = countryCode === 'USA' ? 'US' : countryCode

  // Try to get from cache first
  const locationData = await getLocationData()
  const country = locationData.countries.find(c => c.code === normalizedCode)

  if (country) {
    return country.states
  }

  // If not in cache, fetch specific country data
  const countryData = await getCountryData(countryCode)
  return countryData?.states || []
}

/**
 * Get the list of available countries
 */
export const getCountries = async (): Promise<Country[]> => {
  const locationData = await getLocationData()
  return locationData.countries
}

/**
 * Clear the location data cache
 * Useful when data might have changed
 */
export const clearLocationCache = (): void => {
  locationDataCache = null
  cacheTimestamp = null
}

/**
 * Check if a state code is valid for a given country
 * @param countryCode - Country code
 * @param stateCode - State/province code
 */
export const isValidStateForCountry = async (
  countryCode: string,
  stateCode: string
): Promise<boolean> => {
  const states = await getStatesForCountry(countryCode)
  return states.some(state => state.code === stateCode)
}